import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fmtError } from "@/lib/supabase";
import { getProfile, updateProfile, isUsernameTaken } from "@/lib/events";
import { uploadAvatar, validateAvatar } from "@/lib/avatars";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, MessageCircle, Instagram, Ghost, User as UserIcon,
  FileText, MapPin, Cake, Camera, MailCheck, MailWarning, RefreshCw, X, LogOut,
  Check, Loader2,
} from "lucide-react";
import Avatar from "@/components/Avatar";
import CityPicker from "@/components/CityPicker";

const USERNAME_RE = /^[a-z0-9._-]{3,32}$/;
const GENDERS = ["", "female", "male", "non-binary", "prefer not to say"];

export default function ProfilePage() {
  const { user, refreshProfile, emailVerified, resendConfirmation, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [originalUsername, setOriginalUsername] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resending, setResending] = useState(false);
  const fileRef = useRef(null);

  // Live username availability check — mirrors the SignupPage flow.
  // States: "idle" | "checking" | "available" | "taken" | "invalid"
  // "idle" is also used when the current value equals the user's existing
  // saved username (so the field doesn't flag "available" / "taken" when
  // they haven't actually changed it yet).
  const [usernameStatus, setUsernameStatus] = useState("idle");
  useEffect(() => {
    const u = (profile?.username || "").trim().toLowerCase();
    if (!u) { setUsernameStatus("idle"); return; }
    if (u === originalUsername) { setUsernameStatus("idle"); return; }
    if (!USERNAME_RE.test(u)) { setUsernameStatus("invalid"); return; }
    setUsernameStatus("checking");
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const taken = await isUsernameTaken(u, user?.id);
        if (cancelled) return;
        setUsernameStatus(taken ? "taken" : "available");
      } catch {
        if (!cancelled) setUsernameStatus("idle");
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [profile?.username, originalUsername, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    getProfile(user.id).then((p) => {
      if (!alive) return;
      setOriginalUsername((p?.username || "").toLowerCase());
      setProfile({
        display_name: p?.display_name || "",
        username: p?.username || "",
        full_name: p?.full_name || "",
        birthday: p?.birthday || "",
        gender: p?.gender || "",
        avatar_url: p?.avatar_url || "",
        bio: p?.bio || "",
        city: p?.city || "",
        // v8 location columns (may be undefined on older DB schemas — that's fine).
        state: p?.state || "",
        country: p?.country || "",
        location_lat: p?.location_lat ?? null,
        location_lng: p?.location_lng ?? null,
        whatsapp: p?.whatsapp || "",
        instagram: p?.instagram || "",
        snapchat: p?.snapchat || "",
      });
    });
    return () => { alive = false; };
  }, [user?.id]);

  if (!profile) {
    return (
      <div className="bg-paper min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col gap-3 w-full max-w-md px-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 bg-[#111] border border-ink/15 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const onAvatarPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const err = validateAvatar(f);
    if (err) { toast.error(err); return; }
    setUploading(true);
    try {
      const url = await uploadAvatar(f, user.id);
      set("avatar_url", url);
      await updateProfile(user.id, { avatar_url: url });
      await refreshProfile?.();
      toast.success("Photo updated.");
    } catch (e2) {
      toast.error(fmtError(e2));
    } finally {
      setUploading(false);
    }
  };

  const onRemoveAvatar = async () => {
    if (!profile.avatar_url) return;
    setUploading(true);
    try {
      await updateProfile(user.id, { avatar_url: null });
      set("avatar_url", "");
      await refreshProfile?.();
      toast.success("Photo removed.");
    } catch (e2) {
      toast.error(fmtError(e2));
    } finally {
      setUploading(false);
    }
  };

  const onResend = async () => {
    setResending(true);
    const res = await resendConfirmation();
    setResending(false);
    if (res.ok) toast.success("Verification email sent.");
    else toast.error(res.error || "Could not resend.");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const uname = profile.username.trim().toLowerCase();
    if (!profile.full_name.trim()) { toast.error("Full name is required."); return; }
    if (!USERNAME_RE.test(uname)) {
      toast.error("Username: 3–32 chars, lowercase, letters/numbers/'.','_','-'.");
      return;
    }
    // Front-load the same guards used by signup so the user can never hit
    // Save in a definitely-failing state.
    if (usernameStatus === "checking") {
      toast.error("Hold on — checking that username.");
      return;
    }
    if (usernameStatus === "taken") {
      toast.error("That username is taken. Pick another.");
      return;
    }
    setSaving(true);
    try {
      if (uname !== originalUsername) {
        // Final pre-flight race-loss check.
        const taken = await isUsernameTaken(uname, user.id);
        if (taken) {
          setUsernameStatus("taken");
          toast.error("That username is taken.");
          setSaving(false);
          return;
        }
      }
      await updateProfile(user.id, {
        display_name: (profile.display_name.trim() || profile.full_name.trim()),
        full_name: profile.full_name.trim(),
        username: uname,
        birthday: profile.birthday || null,
        gender: profile.gender || null,
        bio: profile.bio.trim() || null,
        city: profile.city.trim() || null,
        state: profile.state?.trim() || null,
        country: profile.country?.trim() || null,
        location_lat: profile.location_lat ?? null,
        location_lng: profile.location_lng ?? null,
        whatsapp: profile.whatsapp.trim() || null,
        instagram: profile.instagram.trim().replace(/^@/, "") || null,
        snapchat: profile.snapchat.trim().replace(/^@/, "") || null,
      });
      setOriginalUsername(uname);
      await refreshProfile?.();
      toast.success("Profile saved.");
    } catch (err) {
      toast.error(fmtError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      <div className="max-w-2xl mx-auto px-6 md:px-10 pt-8 pb-20">
        <button onClick={() => navigate(-1)} data-testid="profile-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60 hover:text-ink transition-colors mb-8">
          <ArrowLeft size={14} strokeWidth={2} /> Back
        </button>

        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-ink">Your profile</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-none">
              How others see you.
            </h1>
          </div>
          {originalUsername && (
            <Link to={`/u/${originalUsername}`} data-testid="view-public-profile"
              className="border border-ink text-ink hover:bg-paper hover:text-ink px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors">
              View public
            </Link>
          )}
        </div>

        {/* Email verification badge */}
        <div
          data-testid="email-verify-strip"
          className={`mb-10 border p-4 flex items-center gap-3 ${
            emailVerified ? "border-ink/15 text-ink/70" : "border-neon/60 bg-neon/5"
          }`}
        >
          {emailVerified ? (
            <MailCheck size={16} className="text-ink shrink-0" />
          ) : (
            <MailWarning size={16} className="text-ink shrink-0" />
          )}
          <div className="flex-1 text-sm">
            <p className={`uppercase tracking-widest text-xs ${emailVerified ? "text-ink/55" : "text-ink"}`}>
              {emailVerified ? "Email verified" : "Email not verified"}
            </p>
            <p className="text-ink/75 mt-0.5 truncate">{user?.email}</p>
          </div>
          {!emailVerified && (
            <button
              type="button"
              onClick={onResend}
              disabled={resending}
              data-testid="profile-resend-verify"
              className="shrink-0 inline-flex items-center gap-2 bg-neon text-ink px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60"
            >
              <RefreshCw size={12} strokeWidth={2.2} />
              {resending ? "…" : "Resend"}
            </button>
          )}
        </div>

        {/* Avatar uploader */}
        <div className="mb-12 flex items-center gap-6">
          <Avatar
            src={profile.avatar_url || null}
            name={profile.full_name || profile.display_name}
            size="xl"
            glow
            testid="profile-avatar"
          />
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onAvatarPick}
              data-testid="avatar-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="avatar-upload-btn"
              className="inline-flex items-center gap-2 bg-neon text-ink px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60"
            >
              <Camera size={14} strokeWidth={2.2} />
              {uploading ? "Uploading…" : profile.avatar_url ? "Change photo" : "Upload photo"}
            </button>
            {profile.avatar_url && (
              <button
                type="button"
                onClick={onRemoveAvatar}
                disabled={uploading}
                data-testid="avatar-remove-btn"
                className="inline-flex items-center gap-2 border border-ink/15 hover:border-neon hover:text-ink text-ink/70 px-4 py-2 text-xs uppercase tracking-widest transition-colors"
              >
                <X size={12} strokeWidth={2} /> Remove
              </button>
            )}
            <span className="text-[11px] text-ink/45">JPG / PNG / WEBP up to 5 MB.</span>
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="profile-form" className="flex flex-col gap-8">
          <Field label="Full name" icon={UserIcon}>
            <input type="text" required maxLength={80} value={profile.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              data-testid="profile-fullname" className="chunky-input" />
          </Field>

          <Field label="Username" icon={UserIcon}>
            <div className="flex items-center gap-2 border-b border-ink/15 focus-within:border-neon transition-colors">
              <span className="text-ink/50">@</span>
              <input
                type="text"
                required
                maxLength={32}
                value={profile.username}
                onChange={(e) => set("username", e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""))}
                data-testid="profile-username"
                className="chunky-input border-0 px-0 flex-1"
                aria-invalid={usernameStatus === "taken" || usernameStatus === "invalid"}
              />
              {profile.username && usernameStatus !== "idle" && (
                <span
                  data-testid={`profile-username-status-${usernameStatus}`}
                  className="shrink-0 inline-flex items-center justify-center w-6 h-6"
                  aria-live="polite"
                >
                  {usernameStatus === "checking" && (
                    <Loader2 size={16} className="animate-spin text-ink/55" />
                  )}
                  {usernameStatus === "available" && (
                    <Check size={18} strokeWidth={3} className="text-lime-600" />
                  )}
                  {(usernameStatus === "taken" || usernameStatus === "invalid") && (
                    <X size={18} strokeWidth={3} className="text-destructive" />
                  )}
                </span>
              )}
            </div>
            <span
              className={
                "text-[11px] mt-1.5 " +
                (usernameStatus === "taken"
                  ? "text-destructive font-semibold"
                  : usernameStatus === "available"
                  ? "text-lime-700 font-semibold"
                  : "text-ink/45")
              }
              data-testid="profile-username-hint"
            >
              {usernameStatus === "taken"
                ? "That username is taken. Try another."
                : usernameStatus === "available"
                ? `@${profile.username} is available.`
                : usernameStatus === "checking"
                ? "Checking availability…"
                : `Your profile URL · inonit.app/u/${profile.username || "yourname"}`}
            </span>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Birthday" icon={Cake}>
              <input type="date" value={profile.birthday}
                onChange={(e) => set("birthday", e.target.value)}
                data-testid="profile-birthday" className="chunky-input" />
            </Field>
            <Field label="Gender">
              <select value={profile.gender}
                onChange={(e) => set("gender", e.target.value)}
                data-testid="profile-gender" className="chunky-input bg-paper">
                {GENDERS.map((g) => (
                  <option key={g} value={g} className="bg-paper text-ink">
                    {g ? g.charAt(0).toUpperCase() + g.slice(1) : "—"}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="City" icon={MapPin}>
            <CityPicker
              value={{
                city: profile.city,
                state: profile.state,
                country: profile.country,
                lat: profile.location_lat,
                lng: profile.location_lng,
                display: [profile.city, profile.state, profile.country].filter(Boolean).join(", "),
              }}
              onChange={(c) => {
                if (!c) return;
                setProfile((p) => ({
                  ...p,
                  city: c.city || "",
                  state: c.state || "",
                  country: c.country || "",
                  location_lat: c.lat ?? null,
                  location_lng: c.lng ?? null,
                }));
              }}
              testidPrefix="profile-city"
            />
            <span className="text-[11px] text-ink/45 mt-1.5">
              Used to surface nearby events on your feed and on the map.
            </span>
          </Field>

          <Field label="Bio" icon={FileText}>
            <textarea rows={3} maxLength={300} value={profile.bio}
              onChange={(e) => set("bio", e.target.value)}
              placeholder="One line on what you're into."
              data-testid="profile-bio" className="chunky-input resize-y" />
          </Field>

          <Field label="WhatsApp (optional)" icon={MessageCircle}>
            <input type="tel" maxLength={32} value={profile.whatsapp}
              onChange={(e) => set("whatsapp", e.target.value)}
              placeholder="+91 98765 43210"
              data-testid="profile-whatsapp" className="chunky-input" />
          </Field>

          <Field label="Instagram (optional)" icon={Instagram}>
            <input type="text" maxLength={32} value={profile.instagram}
              onChange={(e) => set("instagram", e.target.value)}
              placeholder="yourhandle"
              data-testid="profile-instagram" className="chunky-input" />
          </Field>

          <Field label="Snapchat (optional)" icon={Ghost}>
            <input type="text" maxLength={32} value={profile.snapchat}
              onChange={(e) => set("snapchat", e.target.value)}
              placeholder="yourhandle"
              data-testid="profile-snapchat" className="chunky-input" />
          </Field>

          <button
            type="submit"
            disabled={saving || usernameStatus === "checking" || usernameStatus === "taken"}
            data-testid="profile-submit"
            className="cta-primary w-full mt-4">
            {saving ? "Saving…" : "Save profile"}
          </button>
        </form>

        {/* Account actions */}
        <div className="mt-12 pt-8 border-t-2 border-dashed border-ink/15">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-ink/55 mb-3">Account</p>
          <Link
            to="/settings"
            data-testid="profile-settings-link"
            className="cta-ghost w-full mb-3"
          >
            Settings, feedback &amp; delete account →
          </Link>
          <button
            type="button"
            onClick={async () => { await signOut(); navigate("/"); }}
            data-testid="profile-logout"
            className="cta-ghost w-full text-destructive"
            style={{ borderColor: "hsl(var(--destructive))" }}
          >
            <LogOut size={16} strokeWidth={2.5} /> Log out
          </button>
        </div>
      </div>

      <style>{`
        .bare-input {
          background: transparent; border: none;
          border-bottom: 1px solid hsl(var(--border));
          color: #f4f4f0; padding: 12px 0; width: 100%;
          font-family: inherit; font-size: 1rem; outline: none;
          transition: border-color 200ms;
        }
        .bare-input:focus { border-bottom-color: #d6ff00; border-bottom-width: 2px; }
        .bare-input::placeholder { color: rgba(244,244,240,0.35); }
        select.bare-input { appearance: none; cursor: pointer; padding-right: 2rem;
          background-image: linear-gradient(45deg, transparent 50%, #d6ff00 50%), linear-gradient(-45deg, transparent 50%, #d6ff00 50%);
          background-position: calc(100% - 14px) 18px, calc(100% - 9px) 18px;
          background-size: 5px 5px, 5px 5px; background-repeat: no-repeat;
        }
        input[type="date"].bare-input { color-scheme: dark; }
      `}</style>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60">
        {Icon && <Icon size={12} strokeWidth={1.8} className="text-ink" />} {label}
      </span>
      {children}
    </label>
  );
}
